import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronRight } from "lucide-react";
import { useIsMobile } from "@/hooks/use-is-mobile";

// One-time, mobile-only "swipe to see more" nudge for first-time visitors. Shown once per
// browser (persisted in localStorage), auto-dismisses after a few seconds, and also clears
// the moment the visitor scrolls the carousel (they've discovered it). Hidden at md+.
export const SwipeHint = ({ storageKey, scrollerRef, testid = "carousel" }) => {
  const isMobile = useIsMobile();
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (!isMobile) return;
    if (localStorage.getItem(storageKey)) return;
    const dismiss = () => {
      setShow(false);
      localStorage.setItem(storageKey, "1");
    };
    setShow(true);
    const el = scrollerRef?.current;
    el?.addEventListener("scroll", dismiss, { passive: true, once: true });
    const t = setTimeout(dismiss, 4500);
    return () => {
      clearTimeout(t);
      el?.removeEventListener("scroll", dismiss);
    };
  }, [isMobile, storageKey, scrollerRef]);

  const hide = () => {
    setShow(false);
    localStorage.setItem(storageKey, "1");
  };

  return (
    <AnimatePresence>
      {show && (
        <motion.button
          type="button"
          data-testid={`${testid}-swipe-hint`}
          aria-label="Swipe to see more"
          onClick={hide}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 8 }}
          transition={{ duration: 0.4 }}
          className="md:hidden absolute bottom-4 left-1/2 -translate-x-1/2 z-20 inline-flex items-center gap-2 bg-navy/90 backdrop-blur-sm text-white text-[11px] font-mono uppercase tracking-wider px-3.5 py-2 rounded-full shadow-xl whitespace-nowrap pointer-events-auto"
        >
          Swipe to see more
          <motion.span
            animate={{ x: [0, 7, 0] }}
            transition={{ repeat: Infinity, duration: 1.1, ease: "easeInOut" }}
          >
            <ChevronRight className="h-4 w-4" />
          </motion.span>
        </motion.button>
      )}
    </AnimatePresence>
  );
};
